import React from 'react';

import { Box } from 'ml-rubiks-styleguide/src/box';
import {
    IconAccountBalance,
    IconAddCard
} from 'ml-rubiks-styleguide/src/icon';

import {
    AchIcon,
    BacsIcon,
    CreditCardIcon,
    IdealIcon,
    PaypalIcon,
    SepaIcon,
    TwintIcon
} from '../components/common';
import { PaymentMethod } from '../types';

export interface PaymentMethodConfig {
    name: string;
    description: string;
    icon: React.ComponentType<{
        className?: string;
        [key: string]: unknown;
    }>;
    displayInfo: {
        subtitle: string;
    };
}

export const PAYMENT_METHOD_CONFIG: Record<PaymentMethod, PaymentMethodConfig> =
    {
        CREDIT_CARD: {
            name: 'Credit Card',
            description: 'Pay with Visa, Mastercard, or other credit cards',
            icon: CreditCardIcon,
            displayInfo: {
                subtitle: 'Credit/Debit Card'
            }
        },
        PAYPAL: {
            name: 'PayPal',
            description: 'Pay securely with your PayPal account',
            icon: PaypalIcon,
            displayInfo: {
                subtitle: 'PayPal Account'
            }
        },
        BANCONTACT: {
            name: 'Bancontact',
            description: 'Belgian payment method',
            icon: IconAddCard,
            displayInfo: {
                subtitle: 'Bancontact Card'
            }
        },
        IDEAL: {
            name: 'iDEAL',
            description: 'Dutch bank transfer',
            icon: IdealIcon,
            displayInfo: {
                subtitle: 'iDEAL Bank Transfer'
            }
        },
        BACS: {
            name: 'BACS',
            description: 'UK Direct Debit',
            icon: BacsIcon,
            displayInfo: {
                subtitle: 'BACS Direct Debit'
            }
        },
        BECS: {
            name: 'BECS',
            description: 'Australian BECS Direct Debit',
            icon: IconAccountBalance,
            displayInfo: {
                subtitle: 'BECS Direct Debit'
            }
        },
        TWINT: {
            name: 'TWINT',
            description: 'Swiss mobile payment',
            icon: TwintIcon,
            displayInfo: {
                subtitle: 'TWINT Payment'
            }
        },
        SEPA: {
            name: 'SEPA',
            description: 'Single Euro Payments Area',
            icon: SepaIcon,
            displayInfo: {
                subtitle: 'SEPA Payment'
            }
        },
        CASH: {
            name: 'Cash',
            description: 'Pay with cash',
            icon: IconAccountBalance,
            displayInfo: {
                subtitle: 'Cash Payment'
            }
        },
        BANK_TRANSFER: {
            name: 'Bank Transfer',
            description: 'Pay via bank transfer',
            icon: IconAccountBalance,
            displayInfo: {
                subtitle: 'Bank Transfer Payment'
            }
        },
        CH_DD: {
            name: 'Swiss Direct Debit',
            description: 'Swiss direct debit payment',
            icon: IconAccountBalance,
            displayInfo: {
                subtitle: 'Swiss Direct Debit'
            }
        },
        LSV: {
            name: 'LSV Direct Debit',
            description: 'LSV direct debit payment',
            icon: IconAccountBalance,
            displayInfo: {
                subtitle: 'LSV Direct Debit'
            }
        },
        ACH: {
            name: 'ACH',
            description: 'US Automated Clearing House Direct Debit',
            icon: AchIcon,
            displayInfo: {
                subtitle: 'ACH Direct Debit'
            }
        }
    };

export const getPaymentMethodConfig = (
    method: PaymentMethod
): PaymentMethodConfig => {
    return PAYMENT_METHOD_CONFIG[method];
};

export const getPaymentMethodIcon = (
    method: PaymentMethod
): React.ReactNode => {
    const config = getPaymentMethodConfig(method);
    const IconComponent = config.icon;
    return (
        <Box sx={{ width: '2.5rem' }}>
            <IconComponent color="text.secondary" />
        </Box>
    );
};

export const getPaymentMethodDisplayInfo = (method: PaymentMethod) => {
    return getPaymentMethodConfig(method).displayInfo;
};

export const PAYMENT_METHOD_DESCRIPTIONS: Record<PaymentMethod, string> =
    Object.fromEntries(
        Object.entries(PAYMENT_METHOD_CONFIG).map(([key, config]) => [
            key,
            config.description
        ])
    ) as Record<PaymentMethod, string>;
